/**
 * Unified Structured Logger - Enterprise Grade
 * Implementa logging padronizado com todos os campos obrigatórios
 */

import { shouldSample } from './log-sampling';
import { redactPII } from './pii-redaction';
import { getDeviceInfo } from './device-info';
import { logToNative } from './native-logger';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

export interface UnifiedLog {
  timestamp: string;           // ISO8601 UTC
  level: LogLevel;
  message: string;
  correlation_id: string;      // Propagado do cliente
  request_id?: string;         // Gerado por request
  user_id?: string;
  session_id?: string;
  platform: 'ios' | 'android' | 'web' | 'edge' | 'server';
  app_version: string;
  build_number: string;
  os_version?: string;
  device_model?: string;
  feature: string;             // Ex: "auth.login", "run.tracking"
  context: Record<string, any>;
  duration_ms?: number;
  error?: {
    code?: string;
    stack?: string;
    name: string;
    message: string;
  };
}

class UnifiedLogger {
  private correlationId: string;
  private sessionId: string;
  private userId?: string;
  private context: Record<string, any> = {};
  private level: LogLevel = 'INFO';

  constructor() {
    this.correlationId = this.generateId();
    this.sessionId = this.generateId();
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public setCorrelationId(id: string) {
    this.correlationId = id;
  }

  public getCorrelationId(): string {
    return this.correlationId;
  }

  public setSessionId(id: string) {
    this.sessionId = id;
  }

  public setUserId(id: string | undefined) {
    this.userId = id;
  }

  public setContext(context: Record<string, any>) {
    this.context = { ...this.context, ...context };
  }

  public setLevel(level: LogLevel) {
    this.level = level;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'FATAL'];
    const currentLevelIndex = levels.indexOf(this.level);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private async log(
    level: LogLevel,
    message: string,
    feature: string,
    context: Record<string, any> = {},
    error?: Error | any
  ) {
    if (!this.shouldLog(level)) {
      return;
    }

    // Aplicar sampling para DEBUG em produção
    if (level === 'DEBUG' && import.meta.env.PROD) {
      if (!shouldSample()) {
        return;
      }
    }

    // Redact PII antes de logar
    const sanitizedContext = redactPII(context);

    // Coletar device info
    const deviceInfo = await getDeviceInfo();

    const logEntry: UnifiedLog = {
      timestamp: new Date().toISOString(),
      level,
      message,
      correlation_id: this.correlationId,
      request_id: context.request_id,
      user_id: this.userId,
      session_id: this.sessionId,
      platform: deviceInfo.platform,
      app_version: deviceInfo.app_version,
      build_number: deviceInfo.build_number,
      os_version: deviceInfo.os_version,
      device_model: deviceInfo.device_model,
      feature,
      context: { ...this.context, ...sanitizedContext },
      duration_ms: context.duration_ms,
    };

    if (error) {
      logEntry.error = {
        name: error.name || 'Error',
        message: error.message || String(error),
        code: error.code,
        stack: error.stack,
      };
    }

    // Em produção, enviar para sink externo
    if (import.meta.env.PROD) {
      // Enviar para Sentry se ERROR/FATAL
      if (level === 'ERROR' || level === 'FATAL') {
        this.sendToSentry(logEntry);
      }
      
      // Log estruturado para console (Logflare/BigQuery vai coletar)
      console.log(JSON.stringify(logEntry));
    } else {
      // Em dev, manter legível
      const emoji = {
        DEBUG: '🔍',
        INFO: 'ℹ️',
        WARN: '⚠️',
        ERROR: '❌',
        FATAL: '💀'
      }[level];
      
      console.log(
        `${emoji} [${level}] [${feature}] ${message}`,
        {
          correlation_id: this.correlationId,
          ...sanitizedContext,
          ...(error && { error })
        }
      );
    }

    // Enviar para native logging se disponível
    if (level === 'ERROR' || level === 'FATAL') {
      try {
        logToNative(level === 'FATAL' ? 'fatal' : 'error', message, sanitizedContext);
      } catch {
        // Native logger não disponível
      }
    }
  }

  private sendToSentry(logEntry: UnifiedLog) {
    try {
      const Sentry = (window as any).Sentry;
      if (Sentry) {
        if (logEntry.error) {
          Sentry.captureException(new Error(logEntry.error.message), {
            level: logEntry.level.toLowerCase(),
            contexts: {
              app: {
                version: logEntry.app_version,
                build: logEntry.build_number,
              },
              device: {
                model: logEntry.device_model,
                os_version: logEntry.os_version,
              },
            },
            tags: {
              feature: logEntry.feature,
              platform: logEntry.platform,
              correlation_id: logEntry.correlation_id,
            },
            extra: logEntry.context,
          });
        } else {
          Sentry.captureMessage(logEntry.message, {
            level: logEntry.level.toLowerCase(),
            tags: {
              feature: logEntry.feature,
              correlation_id: logEntry.correlation_id,
            },
          });
        }
      }
    } catch (err) {
      console.error('Failed to send to Sentry:', err);
    }
  }

  public debug(message: string, feature: string, context?: Record<string, any>) {
    this.log('DEBUG', message, feature, context);
  }

  public info(message: string, feature: string, context?: Record<string, any>) {
    this.log('INFO', message, feature, context);
  }

  public warn(message: string, feature: string, context?: Record<string, any>) {
    this.log('WARN', message, feature, context);
  }

  public error(
    message: string,
    feature: string,
    error?: Error | any,
    context?: Record<string, any>
  ) {
    this.log('ERROR', message, feature, context, error);
  }

  public fatal(
    message: string,
    feature: string,
    error?: Error | any,
    context?: Record<string, any>
  ) {
    this.log('FATAL', message, feature, context, error);
  }

  // Helper para criar span de ação
  public startSpan(feature: string, action: string, context?: Record<string, any>) {
    const spanId = this.generateId();
    const requestId = this.generateId();
    const startTime = Date.now();
    
    this.info(`[START] ${action}`, feature, { 
      ...context, 
      span_id: spanId,
      request_id: requestId 
    });
    
    return {
      spanId,
      requestId,
      end: (status: 'success' | 'error', resultContext?: Record<string, any>) => {
        const duration = Date.now() - startTime;
        const finalContext = {
          ...context,
          ...resultContext,
          span_id: spanId,
          request_id: requestId,
          duration_ms: duration,
        };
        
        if (status === 'success') {
          this.info(`[END] ${action}`, feature, finalContext);
        } else {
          this.error(`[FAILED] ${action}`, feature, undefined, finalContext);
        }
      }
    };
  }

  // Coletar logs recentes (para diagnostic report)
  private recentLogs: UnifiedLog[] = [];
  private maxRecentLogs = 100;

  public async getRecentLogs(timeWindowMs: number): Promise<UnifiedLog[]> {
    const cutoff = Date.now() - timeWindowMs;
    return this.recentLogs.filter(log => 
      new Date(log.timestamp).getTime() >= cutoff
    );
  }
}

// Singleton global
export const unifiedLogger = new UnifiedLogger();

// Helpers para compatibilidade com logger antigo
export const logger = {
  setCorrelationId: (id: string) => unifiedLogger.setCorrelationId(id),
  getCorrelationId: () => unifiedLogger.getCorrelationId(),
  setContext: (ctx: Record<string, any>) => unifiedLogger.setContext(ctx),
  debug: (message: string, meta?: any) => 
    unifiedLogger.debug(message, meta?.feature || 'app', meta),
  info: (message: string, meta?: any) => 
    unifiedLogger.info(message, meta?.feature || 'app', meta),
  warn: (message: string, meta?: any) => 
    unifiedLogger.warn(message, meta?.feature || 'app', meta),
  error: (message: string, error?: Error | any, meta?: any) => 
    unifiedLogger.error(message, meta?.feature || 'app', error, meta),
  startSpan: (action: string, meta?: any) => 
    unifiedLogger.startSpan(meta?.feature || 'app', action, meta),
};

// Helper para extrair correlation headers
export function getCorrelationHeaders() {
  return {
    'X-Correlation-ID': unifiedLogger.getCorrelationId(),
  };
}
